<template>
    <div class=" relative mt-[8rem] w-[280px]"  
        @mouseleave="handleMenuLeave">
        <div ref="dropdownButton" 
            class="category-button bg-white text-gray-800 py-4 rounded-md border border-gray-300  hover:bg-gray-50  transition-colors" 
            :class="{ 'show-dropdown': showDropdown,'show-dropdown-btn': showDropdown }"
            @mouseenter="handleMouseEnter">
            Packaging By Category
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" >
                <circle cx="5" cy="7" r="1.5" fill="currentColor" />
                <rect x="9" y="6" width="10" height="2" rx="1" fill="currentColor" />
                <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                <rect x="9" y="11" width="10" height="2" rx="1" fill="currentColor" />
                <circle cx="5" cy="17" r="1.5" fill="currentColor" />
                <rect x="9" y="16" width="10" height="2" rx="1" fill="currentColor" />
            </svg>
        </div>

        <!-- 下拉菜单 -->
        <div  v-if="showDropdown"   
            ref="dropdownList"
            :class="{ 'show-dropdown': showDropdown ,'show-dropdown-menu': showDropdown }"
            class="absolute top-full left-0  w-[300px] bg-white border border-gray-200 rounded-md  z-50">
            <!-- 一级分类循环 -->
            <div v-for="item in navItemList" :key="item.id" 
                class="first-level-menu border-b border-gray-100 " >
                <div class="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50"
                    @click="toggleMenuItem(item)">
                    <span class="font-weight-400 text-gray-800">{{ item.name }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" :class="[item.isOpen ? 'transform rotate-180' : '', 'h-4 w-4 text-gray-500']" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                <!-- 二级分类 -->
                <transition name="dropdown" @enter="enter" @leave="leave">
                    <div v-if="item.isOpen" class="submenu-container pl-6 pr-3  bg-gray-50">
                        <div v-for="subCat in item.children" :key="subCat.id" 
                            class="second-level-menu py-1 hover:text-green-600 cursor-pointer" >
                            <nuxt-link :to="subCat.linkUrl" 
                                :class="route.path === subCat.linkUrl ? 'text-sm text-green-600 font-weight-600' : 'text-sm text-gray-700 hover:text-green-600 hover:font-bold'">
                                {{ subCat.name }}
                            </nuxt-link>
                        </div>
                    </div>
                </transition>
            </div>
        </div>    
        
    </div>
</template>


<script setup lang="ts" name="DrawdownMenu">
    import { ref, onMounted, onUnmounted, computed } from 'vue'
    import cmsUtils from '~/utils/cmsUtils';
    import type {NavItemVo } from '~/types';
    import { useDropdownMenu } from '~/views/leaflypackaging-com/store/useDropdownMenu';
    
    const route = useRoute()
    const cmsInfo = await cmsUtils.getCmsInfo()
    
    // 使用下拉菜单访问状态管理
    const { hasVisited, markAsVisited, isVisited } = useDropdownMenu()
    
    // 根据访问状态决定初始显示状态
    // 第一次访问：true（展开），第二次及以后：false（收起）
    const showDropdown = ref(!isVisited())
    const dropdownButton = ref<HTMLElement | null>(null)
    const dropdownList = ref<HTMLElement | null>(null)
    
    // 是否为第一次访问
    const isFirstVisit = computed(() => !isVisited())

    const navItemList = ref<NavItemVo[]>(cmsUtils.getNavItemTreeByNavIndex("index_category", cmsInfo) as NavItemVo[])
    
    // 鼠标进入按钮或菜单区域
    const handleMouseEnter = () => {
        showDropdown.value = true
    }
    
    // 鼠标离开菜单区域（包括按钮和下拉菜单）
    const handleMenuLeave = () => {
        // 如果不是第一次访问，鼠标离开时自动隐藏
        if (!isFirstVisit.value) {
            showDropdown.value = false
        }
    }


    // 手风琴效果：切换菜单项展开状态
    const toggleMenuItem = (clickedItem: any) => {
        // 如果点击的是当前已展开的项目，则收起它
        if (clickedItem.isOpen) {
            clickedItem.isOpen = false
        } else {
            // 先展开点击的菜单项
            clickedItem.isOpen = true
            
            // 延迟后收起其他菜单项，让展开动画先完成
            setTimeout(() => {
                navItemList.value.forEach(item => {
                    if (item !== clickedItem && item.isOpen) {
                        item.isOpen = false
                    }
                })
            }, 200) // 延迟 200ms，让展开动画先执行
        }
    }

    // menu 根据url默认展开的是对应的菜单项
    watchEffect(() => {
        if(route.path=="/packaging"){
            navItemList.value[0]!.isOpen = true
            return
        }
        if(route.path.includes('/tags/child-resistant')){
            navItemList.value[0]!.isOpen = true
            return
        }
         // 先收起所有菜单项
         navItemList.value.forEach(item => { 
            item.isOpen = false
            if (!item.linkUrl) return
            let path  =item.linkUrl.replace('.html','')
            if(route.path.includes(path)){
                item.isOpen = true
            }
        })
    })

    // 动画进入函数
    const enter = (el: Element, done: () => void) => {
        const element = el as HTMLElement
        element.style.height = '0'
        element.style.overflow = 'hidden'
        element.offsetHeight // 强制重绘
        element.style.height = element.scrollHeight + 'px'
        setTimeout(done, 400) // 动画时长 400ms，更慢更平滑
    }

    // 动画离开函数
    const leave = (el: Element, done: () => void) => {
        const element = el as HTMLElement
        element.style.height = element.scrollHeight + 'px'
        element.style.overflow = 'hidden'
        element.offsetHeight // 强制重绘
        element.style.height = '0'
        setTimeout(done, 400) // 动画时长 400ms，更慢更平滑
    }

    // 点击外部关闭下拉菜单（仅第一次访问时生效）
    const handleClickOutside = (event: MouseEvent) => {
        // 只在第一次访问时，点击外部才关闭
        if (isFirstVisit.value) {
            if (dropdownButton.value && !dropdownButton.value.contains(event.target as Node) && dropdownList.value && !dropdownList.value.contains(event.target as Node)) {
                showDropdown.value = false
                // 标记为已访问
                markAsVisited()
            }
        }
    }

    // 添加和移除事件监听器
    onMounted(() => {
        // 第一次访问时，添加点击外部关闭的监听
        if (isFirstVisit.value) {
            document.addEventListener('click', handleClickOutside)
        }
    })

    onUnmounted(() => {
        // 始终移除事件监听器（如果已添加）
        document.removeEventListener('click', handleClickOutside)
    })
</script>

<style scoped lang="scss">
 .category-button{
        padding-left:8px;
        padding-right:4px;
        width:300px;
        font-size:1.5rem;
        font-weight: 600;
        display:flex;
        align-items: flex-end;
        justify-content: space-around;
    }

    /* 下拉动画过渡效果 */
    .dropdown-enter-active {
        transition: height 0.4s ease-out;
        overflow: hidden;
    }

    .dropdown-leave-active {
        transition: height 0.4s ease-in;
        overflow: hidden;
    }

    .dropdown-enter-from {
        height: 0;
    }

    .dropdown-leave-to {
        height: 0;
    }

    /* 箭头旋转动画 */
    // .h-4.w-4.text-gray-500 {
    //     transition: transform 0.2s cubic-bezier(0, 0, 0, 1);
    // }
    .first-level-menu {
        border-top:solid 1px #cfcfcf
    }
    .first-level-menu:nth-child(1){
        border-top:1px solid #bcbcbc;
    }
    .second-level-menu{
        border-bottom:  dashed 1px #e0e0e0;
    }
    .second-level-menu:last-child{
        border-bottom:none;
    }

    .show-dropdown-menu {
        border-top:none;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
    .show-dropdown-btn {
        border-bottom:none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

</style>